import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaPlus, FaMinus, FaCheck } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import BaseModal from './BaseModal';
import { planRequestsApi } from '../services/endpoints';

const PlanModal = ({ isOpen, onClose, plan, addons }) => {
  const { t } = useTranslation();
  const isCorporate = plan.id === 'corporate';
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleAddonToggle = (addon) => {
    setSelectedAddons(prev => {
      const exists = prev.find(a => a.id === addon.id);
      if (exists) {
        return prev.filter(a => a.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
  };

  const calculateTotal = () => {
    const basePriceMatch = plan.price.match(/[\d,]+/);
    const basePrice = basePriceMatch ? parseInt(basePriceMatch[0].replace(/,/g, '')) : 0;
    
    const addonsTotal = selectedAddons.reduce((sum, addon) => {
      const addonPriceMatch = addon.price.match(/\d+/);
      return sum + (addonPriceMatch ? parseInt(addonPriceMatch[0]) : 0);
    }, 0);

    return basePrice + addonsTotal;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // E1: was a raw axios.post() against
      // `${REACT_APP_BACKEND_URL}/api/plan-requests` — the legacy
      // website-only backend's own env var/endpoint, which no longer
      // exists. Now goes through the canonical api client
      // (src/services/api.js) to the canonical backend's equivalent
      // route (backend/public_routes.py).
      await planRequestsApi.submit({
        ...formData,
        plan: {
          id: plan.id,
          name: plan.name,
          price: plan.price,
        },
        addons: selectedAddons,
        totalPrice: isCorporate ? t('pricing.modal.customQuoteTotal') : `€${calculateTotal().toLocaleString()}`,
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          notes: '',
        });
        setSelectedAddons([]);
      }, 2000);
    } catch (err) {
      setError(t('pricing.modal.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <BaseModal 
      isOpen={isOpen} 
      onClose={onClose}
      maxWidth="max-w-4xl"
    >
      <div className="pt-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8 pr-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">{plan.name}</h2>
          <p className="text-white/60 text-sm sm:text-base">{plan.description}</p>
          <div className="text-3xl sm:text-4xl font-bold gradient-text mt-4">{plan.price}</div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* User Information */}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4">{t('pricing.modal.personalInfo')}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="relative">
                <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors text-sm sm:text-base"
                  placeholder={t('pricing.modal.name')}
                />
              </div>

              <div className="relative">
                <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors text-sm sm:text-base"
                  placeholder={t('pricing.modal.email')}
                />
              </div>

              <div className="relative">
                <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors text-sm sm:text-base"
                  placeholder={t('pricing.modal.phone')}
                />
              </div>

              <div className="relative">
                <FaMapMarkerAlt className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors text-sm sm:text-base"
                  placeholder={t('pricing.modal.address')}
                />
              </div>
            </div>

            <div className="mt-4">
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors resize-none text-sm sm:text-base"
                placeholder={t('pricing.modal.notes')}
              />
            </div>
          </div>

          {/* Add-ons Selection */}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{t('pricing.modal.addons')}</h3>
            <p className="text-white/60 text-xs sm:text-sm mb-4">{t('pricing.modal.addonsNote')}</p>

            <div className="grid sm:grid-cols-2 gap-3">
              {addons.map((addon) => {
                const isSelected = selectedAddons.find(a => a.id === addon.id);
                
                return (
                  <motion.button
                    key={addon.id}
                    type="button"
                    onClick={() => handleAddonToggle(addon)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`glass rounded-xl p-3 sm:p-4 text-left transition-all ${
                      isSelected 
                        ? 'ring-2 ring-[#00d4ff] bg-[#00d4ff]/10' 
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-semibold text-white text-sm sm:text-base">{addon.name}</span>
                          {isSelected && (
                            <FaCheck className="text-[#00d4ff] text-xs sm:text-sm" />
                          )}
                        </div>
                        <p className="text-white/60 text-xs mb-2">{addon.description}</p>
                        <span className="text-[#00d4ff] font-semibold text-xs sm:text-sm">{addon.price}</span>
                      </div>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-[#00d4ff]' : 'bg-white/10'
                      }`}>
                        {isSelected ? <FaMinus className="text-white text-xs" /> : <FaPlus className="text-white/60 text-xs" />}
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Total Price */}
          {isCorporate ? (
            <div className="glass rounded-2xl p-4 sm:p-6">
              <div className="text-white/60 text-xs sm:text-sm mb-1">{t('pricing.modal.total')}</div>
              <div className="text-xl sm:text-2xl font-bold gradient-text mb-2">
                {t('pricing.modal.customQuoteTotal')}
              </div>
              <div className="text-white/60 text-xs sm:text-sm">{t('pricing.modal.customQuoteNote')}</div>
            </div>
          ) : (
            <div className="glass rounded-2xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white/60 text-xs sm:text-sm mb-1">{t('pricing.modal.total')}</div>
                  <div className="text-2xl sm:text-3xl font-bold gradient-text">
                    €{calculateTotal().toLocaleString()}
                  </div>
                </div>
                {selectedAddons.length > 0 && (
                  <div className="text-right">
                    <div className="text-white/60 text-xs">{t('pricing.modal.addonsSelected')}</div>
                    <div className="text-[#00d4ff] font-semibold text-sm">{selectedAddons.length} {t('pricing.modal.items')}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Messages */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 text-red-400 text-center text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-500/10 border border-green-500/50 rounded-xl p-4 text-green-400 text-center text-sm">
              {t('pricing.modal.successMessage')}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary !py-3 sm:!py-4 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('pricing.modal.sending') : t('pricing.modal.submitButton')}
          </button>
        </form>
      </div>
    </BaseModal>
  );
};

export default PlanModal;
